import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EditBenefitsDialogComponent, EditBenefitsDialogData, EditBenefitsDialogResult } from '../payrolladmin/edit-benefits-dialog/edit-benefits-dialog.component';
import { Subscription } from 'rxjs';
import { TeamMember } from '../../interfaces/team/member/member.interface';
import { ManagedFeatureFlagsService } from '../../services/managed-feature-flags/managed-feature-flags.service';
import { TEAM_ADMIN_MANAGE_WELLSPACE_PRODUCTS_FLAG } from '../../services/managed-feature-flags/managed-feature-flag-keys';
import { SessionService } from '../../services/session/session.service';
import { TeamManagerWorkspaceService } from '../../services/team-manager-workspace/team-manager-workspace.service';
import { TeamsService } from '../../services/teams/teams.service';
import { UsersService } from '../../services/users/users.service';

@Component({
  selector: 'app-my-team',
  templateUrl: './my-team.component.html',
  styleUrls: ['./my-team.component.scss'],
})
export class MyTeamComponent implements OnInit, OnDestroy {
  team: any;
  members: TeamMember[] = [];
  membersLoading = false;
  membersError = false;
  productsEnabled = false;
  productCount: number | null = null;
  productsError = false;
  switching = false;
  selectionError = false;
  private readonly subscriptions = new Subscription();
  private requestVersion = 0;
  private selectionVersion = 0;
  private benefitsDialog?: MatDialogRef<EditBenefitsDialogComponent, EditBenefitsDialogResult>;

  constructor(
    public workspace: TeamManagerWorkspaceService,
    private teamsService: TeamsService,
    private flags: ManagedFeatureFlagsService,
    private sessionService: SessionService,
    private usersService: UsersService,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.workspace.stateChanges.subscribe(state => {
      this.showTeam(state.enabled ? this.teamsService.wellspaceChangedObservable.value : null);
    }));
    this.subscriptions.add(this.teamsService.wellspaceChangedObservable.subscribe(team => {
      if (team?.id && this.workspace.managesTeam(team.id)) {
        // The header and this page both publish through TeamsService. A confirmed
        // selection supersedes any older page request, even before it settles.
        this.selectionVersion++;
        this.switching = false;
      }
      this.showTeam(team);
    }));
  }

  ngOnDestroy(): void {
    this.requestVersion++;
    this.selectionVersion++;
    this.subscriptions.unsubscribe();
    this.benefitsDialog?.close();
  }

  private showTeam(team: any): void {
    if (team?.id !== this.team?.id || !this.workspace.managesTeam(team?.id)) this.benefitsDialog?.close();
    if (team?.id && this.workspace.managesTeam(team.id)) {
      if (team === this.team) return;
      this.team = team;
      this.selectionError = false;
      void this.loadDetails();
    } else {
      this.requestVersion++;
      this.team = undefined;
      this.members = [];
      this.membersLoading = false;
      this.membersError = false;
      this.productsEnabled = false;
      this.productCount = null;
      this.productsError = false;
    }
  }

  async selectTeam(teamId: string): Promise<void> {
    if (!this.workspace.managesTeam(teamId)) return;
    const selectionVersion = ++this.selectionVersion;
    this.switching = true;
    this.selectionError = false;
    this.showTeam(null);
    await this.workspace.selectTeam(teamId);
    if (selectionVersion !== this.selectionVersion) return;
    this.switching = false;
    // A superseded request can return null while another selector has kept or
    // changed the canonical team. Never replace that context with its result.
    this.showTeam(this.teamsService.wellspaceChangedObservable.value);
    this.selectionError = !this.team;
  }

  get hasSelectionError(): boolean {
    return this.workspace.state.enabled && !this.team && !this.switching
      && !this.workspace.state.loading
      && (this.selectionError || this.workspace.state.teams.length === 1);
  }

  async retryTeamSelection(): Promise<void> {
    const teams = this.workspace.state.teams;
    if (teams.length === 1) await this.selectTeam(teams[0].id);
  }

  async loadDetails(): Promise<void> {
    const team = this.team;
    if (!team?.id || !this.workspace.managesTeam(team.id)) return;
    const version = ++this.requestVersion;
    const userId = this.sessionService.getUserId();
    const current = () => version === this.requestVersion && this.team?.id === team.id
      && userId === this.sessionService.getUserId() && this.workspace.managesTeam(team.id);
    this.members = [];
    this.membersLoading = true;
    this.membersError = false;
    this.productsEnabled = false;
    this.productsError = false;
    this.productCount = null;
    await Promise.all([
      this.teamsService.getTeamMembers(team.id).then(members => {
        if (current()) this.members = members;
      }).catch(() => {
        if (current()) this.membersError = true;
      }).finally(() => {
        if (current()) this.membersLoading = false;
      }),
      (async () => {
        if (!team.companyId) return;
        try {
          const me = this.usersService.me();
          const enabled = this.sessionService.isSuperAdmin() || await this.flags.isEnabled(TEAM_ADMIN_MANAGE_WELLSPACE_PRODUCTS_FLAG, 'team-admin', {
            userId, teamId: team.id,
            partnerId: me?.user?.partnerId || me?.brand?.partnerId || undefined,
          });
          if (!current() || !enabled) return;
          this.productsEnabled = true;
          const selection = await this.teamsService.getTeamProductSelection(team.id);
          if (current()) this.productCount = selection.selectedProductIds.length;
        } catch (_) {
          if (current()) this.productsError = true;
        }
      })(),
    ]);
  }

  get pendingInvitesCount(): number {
    return this.members.filter(member => !!(member.memberId || member.id)
      && !!(member.userEmail || member.email) && !member.inviteSentAt && !member.inviteAcceptedAt).length;
  }

  get sentInvitesCount(): number { return this.members.filter(member => !!member.inviteSentAt).length; }
  get acceptedInvitesCount(): number { return this.members.filter(member => !!member.inviteAcceptedAt).length; }
  get hasPendingAutomation(): boolean {
    const automation = this.team?.metadata?.inviteAutomation;
    return !!automation?.enabled && (!!automation.initialInviteSendAt || !!automation.nextRetryAt
      || Number(automation.pendingInitialCount || 0) > 0 || Number(automation.pendingRetryCount || 0) > 0);
  }
  get categories(): string[] { return Array.isArray(this.team?.categories) ? this.team.categories : []; }
  memberName(member: TeamMember): string {
    return [member.firstName, member.lastName].filter(Boolean).join(' ') || member.userEmail || member.email;
  }

  openSettings(section = 'overview'): void {
    if (!this.team || !this.workspace.managesTeam(this.team.id) || this.switching) return;
    // Existing screens keep their current role checks, writes, and unsaved-change handling.
    void this.router.navigate(['/managewellspace', section, this.workspace.isSuperadminWorkspace ? this.team.id : 'current'], {
      queryParams: { source: 'my-team' }, queryParamsHandling: 'merge',
    });
  }

  editBenefits(): void {
    const team = this.team;
    if (!team?.id || !this.workspace.managesTeam(team.id) || this.switching || this.benefitsDialog) return;
    const userId = this.sessionService.getUserId();
    const selectionVersion = this.selectionVersion;
    const ref = this.dialog.open<EditBenefitsDialogComponent, EditBenefitsDialogData, EditBenefitsDialogResult>(
      EditBenefitsDialogComponent, {
        data: { teamId: team.id, teamName: team.name, teamManager: true },
        width: '1100px', maxWidth: '96vw', maxHeight: '90vh',
        autoFocus: false, panelClass: 'edit-benefits-dialog-panel',
      },
    );
    this.benefitsDialog = ref;
    const dialogState = ref.componentInstance;
    this.subscriptions.add(ref.afterClosed().subscribe(result => {
      this.benefitsDialog = undefined;
      // Escape/backdrop dismissal supplies no result. A partially saved dialog
      // still needs a refresh, without replacing a newer team/user selection.
      if ((result?.saved || dialogState?.hasPersistedChanges)
        && selectionVersion === this.selectionVersion && userId === this.sessionService.getUserId()
        && this.team?.id === team.id
        && this.workspace.managesTeam(team.id)) {
        void this.selectTeam(team.id);
      }
    }));
  }
}
